import { useEffect } from "react";

import {
  DEFAULT_OG_IMAGE,
  COMPANY_SHORT_NAME,
  buildCanonicalUrl,
  toAbsoluteUrl,
} from "../lib/seo";

const SEO_META_ATTR = "data-buildwit-seo";
const SEO_SCHEMA_ATTR = "data-buildwit-seo-schema";

function upsertMeta(selector, createElement, updates) {
  const element = document.head.querySelector(selector) || createElement();
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
  element.setAttribute(SEO_META_ATTR, "true");
  if (!element.parentNode) {
    document.head.appendChild(element);
  }
}

function ensureMetaByName(name, content) {
  upsertMeta(
    `meta[name="${name}"]`,
    () => {
      const meta = document.createElement("meta");
      meta.setAttribute("name", name);
      return meta;
    },
    { content }
  );
}

function ensureMetaByProperty(property, content) {
  upsertMeta(
    `meta[property="${property}"]`,
    () => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", property);
      return meta;
    },
    { content }
  );
}

function ensureLink(rel, href, extraAttributes = {}) {
  const element = document.head.querySelector(`link[rel="${rel}"]`) || document.createElement("link");
  element.setAttribute("rel", rel);
  element.setAttribute("href", href);
  Object.entries(extraAttributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
  element.setAttribute(SEO_META_ATTR, "true");
  if (!element.parentNode) {
    document.head.appendChild(element);
  }
}

export default function Seo({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  keywords = [],
  noindex = false,
  schema = [],
}) {
  useEffect(() => {
    const canonicalUrl = buildCanonicalUrl(canonicalPath);
    const socialImage = toAbsoluteUrl(image || DEFAULT_OG_IMAGE);
    const robots = noindex
      ? "noindex,nofollow,noarchive"
      : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

    document.title = title;
    document.documentElement.setAttribute("lang", "en-IN");

    ensureMetaByName("description", description);
    ensureMetaByName("robots", robots);
    ensureMetaByName("keywords", keywords.filter(Boolean).join(", "));
    ensureMetaByName("author", COMPANY_SHORT_NAME);
    ensureMetaByName("twitter:card", "summary_large_image");
    ensureMetaByName("twitter:title", title);
    ensureMetaByName("twitter:description", description);
    ensureMetaByName("twitter:image", socialImage);

    ensureMetaByProperty("og:locale", "en_IN");
    ensureMetaByProperty("og:site_name", COMPANY_SHORT_NAME);
    ensureMetaByProperty("og:title", title);
    ensureMetaByProperty("og:description", description);
    ensureMetaByProperty("og:type", type);
    ensureMetaByProperty("og:url", canonicalUrl);
    ensureMetaByProperty("og:image", socialImage);

    ensureLink("canonical", canonicalUrl);
    ensureLink("alternate", canonicalUrl, { hreflang: "en-IN" });

    document.head.querySelectorAll(`script[${SEO_SCHEMA_ATTR}="true"]`).forEach((element) => {
      element.remove();
    });

    schema.filter(Boolean).forEach((item) => {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute(SEO_SCHEMA_ATTR, "true");
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
  }, [canonicalPath, description, image, keywords, noindex, schema, title, type]);

  return null;
}